/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, type MemoExoticComponent } from 'react';

import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';

import type { JiraPeopleField_Platform$key } from './__generated__/JiraPeopleField_Platform.graphql';
import {
	type UserOption,
	UserPickerComponent,
	type UserPickerComponentProps,
} from './UserPickerComponent';

export const JiraPeopleField: MemoExoticComponent<
	({
		field,
		isFieldEditable,
		...props
	}: { field: JiraPeopleField_Platform$key } & Omit<
		UserPickerComponentProps,
		'isMulti' | 'defaultUsers' | 'isRequired' | 'isEditable'
	>) => JSX.Element
> = memo(
	({
		field,
		isFieldEditable = true,
		...props
	}: { field: JiraPeopleField_Platform$key } & Omit<
		UserPickerComponentProps,
		'isMulti' | 'defaultUsers' | 'isRequired' | 'isEditable'
	>): JSX.Element => {
		const data = useFragment<JiraPeopleField_Platform$key>(
			graphql`
				fragment JiraPeopleField_Platform on JiraPeopleField {
					isMulti
					fieldConfig {
						isEditable
						isRequired
					}
					selectedUsersConnection {
						edges {
							node {
								id
								name
								accountId
								picture
							}
						}
					}
				}
			`,
			field,
		);

		const isEditable = (data?.fieldConfig?.isEditable ?? false) && isFieldEditable;
		const isRequired = data?.fieldConfig?.isRequired ?? false;

		const defaultUsers = data?.selectedUsersConnection?.edges
			?.map(
				(edge) =>
					edge?.node?.id && {
						id: edge.node.id,
						name: edge.node.name,
						accountId: edge.node.accountId,
						avatarUrl: edge.node.picture,
					},
			)
			.filter(Boolean) as UserOption[] | undefined;

		return (
			<UserPickerComponent
				{...props}
				isEditable={isEditable}
				isRequired={isRequired}
				defaultUsers={defaultUsers ?? null}
				isMulti={Boolean(data?.isMulti)}
			/>
		);
	},
);
